import { AnimatePresence, motion } from 'framer-motion';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose } from 'react-icons/io5';
import { addItem } from '../../../api/users/addItem';
import { MAX_BRAND_SELECTION, MENSWEAR_SHOPPING_PREFERENCE, WOMENSWEAR_SHOPPING_PREFERENCE } from '../../../common/constants';
import { View } from '../../../common/constants/ViewEnum';
import { useAccountCreationContext } from '../../../common/context/AccountCreationContext';
import { useAuthContext } from '../../../common/context/AuthContext';
import { Brand } from '../../../common/types';
import { trackEvent } from '../../../common/utils/analytics';
import { determineShoppingPreferenceLogic, getShoppingPreferenceFromGender } from '../../../common/utils/shoppingPreferenceLogic';
import PopsizeSpinningLoaderLargeStandard from '../../loader/PopsizeSpinningLoaderLarge-Std';
import WidgetNavigationButtons from '../../modals/WidgetNavigationButtons';
import AddItemView from '../account-creation/AddItemView';
import AnalysisView from '../account-creation/AnalysisView';
import BrandSelectionView from '../account-creation/BrandSelectionView';
import CredentialsView from '../account-creation/CredentialsView';
import NewUserFlow from '../account-creation/NewUserFlow';
import ProductSelectionView from '../account-creation/ProductSelectionView';
import ShoppingPreferenceView from '../account-creation/ShoppingPreferenceView';
import SizeSelectionView from '../account-creation/SizeSelectionView';
import TypeSelectionView from '../account-creation/TypeSelectionView';
import { RViewProps } from '../types';
import { fullScrapeProduct } from '../../../api/products/fullScrape';


const RegisterView: FC<RViewProps & { centerImageUrl: string }> = (props) => {
	const { resetAccountCreationState, setBrands, brands, setItems, items, selectedProducts, setSelectedProducts, setShoppingPreference, setEmail  } = useAccountCreationContext();
	const [brandIndex, setBrandIndex] = useState<number>(0);
	const { t } = useTranslation();
	const [productModels, setProductModels] = useState<any[]>([]);
	const modalContentRef = useRef<HTMLDivElement>(null);
	const [selectedBrands, setSelectedBrands] = useState<Array<Brand>>([]);
	const [brandPhase, setBrandPhase] = useState<'product' | 'size'>('product');
	const isBrandSelected = (brandCode: string) => selectedBrands.map((b) => b.code).includes(brandCode);
	const [isSizeModalOpen, setIsSizeModalOpen] = useState(false);
	const { isAuthenticated, isOnboarded, uid, token } = useAuthContext();
	const [hasInitializedStep, setHasInitializedStep] = useState(false);
	const [genderWarning, setGenderWarning] = useState(false); // New state for gender warning
	const [isFlowLoading, setIsFlowLoading] = useState(true); // New state for flow determination loading
	const [isFlowStable, setIsFlowStable] = useState(false); // New state to track when flow is fully stable
	const flowDeterminationRef = useRef(false); // Prevent multiple simultaneous flow determinations

	// View duration tracking states
	const [currentViewStartTime, setCurrentViewStartTime] = useState<number | null>(null);
	const [previousViewName, setPreviousViewName] = useState<string | null>(null);
	const [sizeViewStartTime, setSizeViewStartTime] = useState<number | null>(null);

	const flowOption = process.env.REACT_APP_FLOW_OPTIONS ?? 'BrandProductCredentials';

	// Define booleans for step visibility
	const showTypeSelection = flowOption.includes('Type');
	const showProductSelection = flowOption.includes('Product');
	const showCredentials = flowOption.includes('Credentials') && !isAuthenticated;

	// Check if this is an Add Item flow or New User Add Item flow
	const isAddItemFlow = props.isAddItemFlow === true;
	const isNewUserAddItemFlow = props.isNewUserAddItemFlow === true;
	const maxBrandSelection = (isAddItemFlow || isNewUserAddItemFlow) ? 1 : MAX_BRAND_SELECTION;

	// Helper function to get view name from step
	const getViewName = (step: number): string | null => {
		switch (step) {
			case 0:
				if (isNewUserAddItemFlow) return 'new_user_view';
				if (isAddItemFlow) return 'add_item_view';
				return null; // for create account, step 0 is not used
			case 2:
				return 'shopping_pref_view';
			case 4:
				return 'brand_selection_view';
			case 5:
				if (showTypeSelection) return 'type_selection_view';
				return 'product_selection_view';
			case 6:
				return 'credentials_view';
			case 7:
				return 'analysis_view';
			default:
				return null;
		}
	};

	// Log which flow we're in
	useEffect(() => {
		if (isNewUserAddItemFlow) {
			console.debug(`🔄 [FLOW] NEW_USER_ADD_ITEM flow initialized`);
		} else if (isAddItemFlow) {
			console.debug(`🔄 [FLOW] ADD_ITEM flow initialized`);
		} else {
			console.debug(`🔄 [FLOW] CREATE_ACCOUNT flow initialized`);
		}
		console.debug(`🔄 [FLOW] isAuthenticated: ${isAuthenticated}, isOnboarded: ${isOnboarded}`);
		console.debug(`🔄 [FLOW] maxBrandSelection: ${maxBrandSelection}`);
		console.debug(`🔄 [FLOW] genderSpecific: ${props.genderSpecific}, genderLabel: ${props.genderLabel}`);
	}, [isAddItemFlow, isAuthenticated, isOnboarded, maxBrandSelection, props.genderSpecific, props.genderLabel]);

	// Sync flow loading state changes to parent
	useEffect(() => {
		props.onFlowLoadingChange?.(isFlowLoading);
	}, [isFlowLoading, props.onFlowLoadingChange]);

	/**
	 * Main initialization effect that determines the initial step and shopping preference
	 * Uses the new comprehensive gender logic for CREATE_ACCOUNT flow
	 * Handles 6 different conditions based on brand gender_specific, user auth status, and gender matching
	 */
	useEffect(() => {
		if (!props.modalIsOpen) return;
		
		// Prevent multiple simultaneous flow determinations
		if (flowDeterminationRef.current) {
			console.debug('🔄 [FLOW_DETERMINATION] Already running, skipping...');
			return;
		}

		// Start with loading state
		setIsFlowLoading(true);
		setIsFlowStable(false);
		props.onFlowLoadingChange?.(true); // Notify parent
		flowDeterminationRef.current = true;

		// Comprehensive flow determination with actual backend checks
		const determineFlow = async () => {
			try {
				console.debug('🔄 [FLOW_DETERMINATION] Starting comprehensive flow analysis...');
				
				// Step 1: Wait for authentication state to be stable
				console.debug('🔄 [FLOW_DETERMINATION] Step 1: Checking authentication state...');
				await new Promise(resolve => setTimeout(resolve, 300)); // Allow auth context to settle
				
				// Step 2: Determine flow type (ADD_ITEM vs NEW_USER_ADD_ITEM vs CREATE_ACCOUNT)
				console.debug('🔄 [FLOW_DETERMINATION] Step 2: Determining flow type...');
				if (isNewUserAddItemFlow) {
					console.debug('🔧 [NEW_USER_ADD_ITEM] Setting up New User Add Item flow initial step');
					
					// Reset state for NEW_USER_ADD_ITEM flow to ensure clean start
					console.debug('🧹 [NEW_USER_ADD_ITEM] Resetting state for fresh NEW_USER_ADD_ITEM flow');
					resetAddItemState();
					
					// Step 3: Determine shopping preference based on gender
					console.debug('🔄 [FLOW_DETERMINATION] Step 3: Setting shopping preference for NEW_USER_ADD_ITEM...');
					if (props.genderLabel === 'male') {
						setShoppingPreference(MENSWEAR_SHOPPING_PREFERENCE);
						console.debug('🔧 [NEW_USER_ADD_ITEM] Set shopping preference to menswear');
					} else if (props.genderLabel === 'female') {
						setShoppingPreference(WOMENSWEAR_SHOPPING_PREFERENCE);
						console.debug('🔧 [NEW_USER_ADD_ITEM] Set shopping preference to womenswear');
					}
					
					// Step 4: Set initial step
					props.setStep(0); // Start with NewUserFlow first
					console.debug('🔧 [NEW_USER_ADD_ITEM] Starting at step 0 (NewUserFlow)');
					
				} else if (isAddItemFlow) {
					console.debug('🔧 [ADD_ITEM] Setting up Add Item flow initial step');
					
					// Reset state for ADD_ITEM flow to ensure clean start
					console.debug('🧹 [ADD_ITEM] Resetting state for fresh ADD_ITEM flow');
					resetAddItemState();
					
					// Step 3: Determine shopping preference based on gender
					console.debug('🔄 [FLOW_DETERMINATION] Step 3: Setting shopping preference for ADD_ITEM...');
					if (props.genderLabel === 'male') {
						setShoppingPreference(MENSWEAR_SHOPPING_PREFERENCE);
						console.debug('🔧 [ADD_ITEM] Set shopping preference to menswear');
					} else if (props.genderLabel === 'female') {
						setShoppingPreference(WOMENSWEAR_SHOPPING_PREFERENCE);
						console.debug('🔧 [ADD_ITEM] Set shopping preference to womenswear');
					}
					
					// Step 4: Set initial step
					props.setStep(0); // Start with AddItemView first
					console.debug('🔧 [ADD_ITEM] Starting at step 0 (AddItemView)');
					
				} else {
					console.debug('🔧 [CREATE_ACCOUNT] Setting up Create Account flow with new gender logic');
					
					// Step 3: Analyze gender logic and user state
					console.debug('🔄 [FLOW_DETERMINATION] Step 3: Analyzing gender logic...');
					
					// Log gender-related values for debugging
					console.debug('🔍 [GENDER_DEBUG] Gender values:', {
						gender_specific: props.genderSpecific,
						gender_label: props.genderLabel, // This is the PRODUCT gender from visitUrl API
						user_gender_label: isAuthenticated && isOnboarded ? props.genderLabel : undefined, // Only set if user is authenticated
						isAuthenticated,
						isOnboarded,
						isNewUser: !isAuthenticated || !isOnboarded
					});
					
					// Use new comprehensive gender logic
					// For new users: user gender is unknown, product gender comes from API
					// For existing users: user gender could come from profile (for now using same as product)
					const userGender = (isAuthenticated && isOnboarded) ? props.genderLabel : undefined;
					const productGender = props.genderLabel; // This comes from the product page/API
					
					const decision = determineShoppingPreferenceLogic(
						props.genderSpecific,
						isAuthenticated,
						isOnboarded,
						userGender,
						productGender
					);

					console.debug('🔧 [CREATE_ACCOUNT] Gender logic decision:', decision);
					console.debug(`🔧 [CREATE_ACCOUNT] Input parameters:`, {
						genderSpecific: props.genderSpecific,
						isAuthenticated,
						isOnboarded,
						userGenderLabel: userGender,
						productGenderLabel: productGender
					});

					// Step 4: Apply gender logic decisions
					console.debug('🔄 [FLOW_DETERMINATION] Step 4: Applying gender logic decisions...');
					
					// Handle gender warning
					setGenderWarning(decision.setGenderWarning);
					if (decision.setGenderWarning) {
						console.debug('⚠️ [CREATE_ACCOUNT] Gender warning set due to mismatch');
					}

					// Handle user gender setting
					if (decision.shouldSetUserGender && decision.userGenderToSet) {
						const preference = getShoppingPreferenceFromGender(decision.userGenderToSet);
						setShoppingPreference(preference);
						console.debug(`🔧 [CREATE_ACCOUNT] Set user gender to ${decision.userGenderToSet} with preference ${preference}`);
					}

					// Step 5: Determine initial step based on decision
					console.debug('🔄 [FLOW_DETERMINATION] Step 5: Setting initial step...');
					if (decision.showShoppingPreferenceView) {
						props.setStep(2); // Show shopping preference selection
						console.debug('🔧 [CREATE_ACCOUNT] Starting at step 2 (ShoppingPreferenceView)');
					} else {
						props.setStep(4); // Skip to brand selection
						console.debug('🔧 [CREATE_ACCOUNT] Starting at step 4 (BrandSelectionView)');
					}
				}

				// Step 6: Wait for all state updates to propagate
				console.debug('🔄 [FLOW_DETERMINATION] Step 6: Waiting for state updates to propagate...');
				await new Promise(resolve => setTimeout(resolve, 200));

				// Step 7: Mark flow as stable and initialized
				console.debug('🔄 [FLOW_DETERMINATION] Step 7: Flow determination complete - marking as stable');
				setHasInitializedStep(true); // ✅ mark init done
				setIsFlowStable(true); // ✅ mark flow as stable
				
				// Final delay to ensure smooth transition
				await new Promise(resolve => setTimeout(resolve, 300));
				setIsFlowLoading(false); // ✅ flow determination complete
				props.onFlowLoadingChange?.(false); // Notify parent
				
				console.debug('✅ [FLOW_DETERMINATION] Flow is now stable and ready to display screens');

			} catch (error) {
				console.error('❌ [FLOW_DETERMINATION] Error during flow determination:', error);
				// Even on error, we should stop loading to prevent infinite loading state
				setIsFlowLoading(false);
				setIsFlowStable(true);
				setHasInitializedStep(true);
				props.onFlowLoadingChange?.(false); // Notify parent even on error
			} finally {
				// Always reset the ref to allow future flow determinations
				flowDeterminationRef.current = false;
			}
		};

		determineFlow();
	}, [props.modalIsOpen, props.genderLabel, props.genderSpecific]); // Removed isAuthenticated, isOnboarded, isAddItemFlow to prevent multiple runs
	
	/*useEffect(() => {
		if (!props.modalIsOpen) return;

		// Set initial step to shopping preference (if needed)
		if (props.genderSpecific === 'false') {
			props.setStep(2);
		} else {
			props.setStep(4);
		}

		if (props.genderLabel === 'male') {
			setShoppingPreference(MENSWEAR_SHOPPING_PREFERENCE);
		}
		if (props.genderLabel === 'female') {
			setShoppingPreference(WOMENSWEAR_SHOPPING_PREFERENCE);
		}

		setHasInitializedStep(true);
	}, [props.modalIsOpen, props.genderLabel]);*/


  
	useEffect(() => {
		if (isSizeModalOpen) {
			const dialog = document.getElementById('widget-size-modal') as HTMLDialogElement | null;
		if (dialog && !dialog.open) {
			dialog.showModal();
			}
		}
	}, [isSizeModalOpen]);

	/* Handles the selection/unselection of a brand. Max 3 brands selected */
	const toggleBrandSelection = (brand: Brand) => {
		setSelectedBrands((prev) => {
			const isAlreadySelected = prev.some((b) => b.code === brand.code);
			if (isAlreadySelected) {
				return prev.filter((b) => b.code !== brand.code);
			} else if (prev.length < maxBrandSelection) {
				return [...prev, brand];
			}
			return prev;
		});
	};

	const clickNextFromBrandView = async () => {
		if (selectedBrands.length === 0 || !props.typeLabelLong) return;
		console.debug('🏪 [BRAND_SELECTION] Moving to step 5 with selected brands:', selectedBrands);
		setBrands(selectedBrands);
		setBrandIndex(0);
		setBrandPhase('product');
		props.setStep(5); // unified brand handling step
	};

	useEffect(() => {
		console.debug("📦 Brands:", brands);
		console.debug("📦 selectedProducts:", selectedProducts);
		selectedProducts.forEach((prod, idx) => {
			console.debug(`— Brand ${brands[idx]?.name || idx}:`, prod);
		});
	}, [brands, selectedProducts, props.step, brandIndex]);

	useEffect(() => {
		const scrollToTop = () => {
		if (modalContentRef.current) {
			modalContentRef.current.scrollTo(0, 0);
		}
		};
		const timer = setTimeout(scrollToTop, 0);
		return () => clearTimeout(timer);
	}, [props.step]);

	const handleProductSelectedForBrand = (product: any) => {
		if (!product) {
			
			// User clicked "I don’t find my product"
			const updatedProducts = [...selectedProducts];
			updatedProducts[brandIndex] = null; // mark no product
			setSelectedProducts(updatedProducts);

			const updatedItems = [...items];
			updatedItems[brandIndex] = {
			...updatedItems[brandIndex],
			firestoreId: '',
			productImages: [],
			};
			setItems(updatedItems);

			// Move to size modal anyway
			setIsSizeModalOpen(true);
			trackEvent('product_not_selected');
			return;
		}
		// fullScrapeProduct({ firestore_id: product.firestore_id });  // TODO: reactivate as soon as GarmentIQ is live prod

		const normalizedProduct = {
			firestore_id: product.firestore_id,
			product_images: product.product_images ?? [], // ✅ ensure images stay
		};

		const updatedProducts = [...selectedProducts];
		updatedProducts[brandIndex] = normalizedProduct;
		setSelectedProducts(updatedProducts);

		const updatedItems = [...items];
		// ✅ Preserve existing item data and only update firestore and product images
		updatedItems[brandIndex] = {
			...updatedItems[brandIndex], // Keep existing brandCode, typeLabelLong, etc.
			firestoreId: normalizedProduct.firestore_id,
			productImages: normalizedProduct.product_images, // ✅ persist also in items
		};
		setItems(updatedItems);

		console.debug('📦 [PRODUCT_SELECTED] Updated item for brand index', brandIndex, ':', updatedItems[brandIndex]);
		setIsSizeModalOpen(true);
		};


	const handleTypeSelectedForBrand = (product: any) => {
	const updatedProducts = [...selectedProducts];
	updatedProducts[brandIndex] = product;
	setSelectedProducts(updatedProducts);

	const updatedItems = [...items];

	// ✅ OVERWRITE instead of push
	updatedItems[brandIndex] = {
		brandCode: brands[brandIndex]?.code || '',
		firestoreId: '', // default empty, no product selected yet
		sizeLabel: '',   // will be added after size is selected
		typeSubCategory: product?.type_subcategory || '',
		typeLabelLong: product?.type_label_long || '',
	};

	console.debug('🏷️ [TYPE_SELECTED] Initial item setup for brand index', brandIndex, ':', updatedItems[brandIndex]);
	setItems(updatedItems);
	setIsSizeModalOpen(true);
};


	const handleSizeSelectedForBrand = async (completedItem?: any) => {
		// If we have a completed item, ensure it's properly stored in the items array
		if (completedItem) {
			const updatedItems = [...items];
			updatedItems[brandIndex] = completedItem;
			setItems(updatedItems);
			console.debug('📝 [SIZE_SELECTED] Updated item for brand index', brandIndex, ':', completedItem);
		}
		
		trackEvent('size_selected');
		
		if (brandIndex < brands.length - 1) {
			setBrandIndex(brandIndex + 1);
			setBrandPhase('product');
		} else {
			// Final step - handle completion differently for Add Item flows vs Create Account
			if (isAddItemFlow || isNewUserAddItemFlow) {
				const flowType = isNewUserAddItemFlow ? 'NEW_USER_ADD_ITEM' : 'ADD_ITEM';
				
				// Wait a bit for state updates to complete
				await new Promise(resolve => setTimeout(resolve, 100));
				
				// Get the most current items array (including the just-completed item)
				const currentItems = completedItem ? 
					[...items.slice(0, brandIndex), completedItem, ...items.slice(brandIndex + 1)] :
					items;
				
				console.debug(`✅ [${flowType}] Completing ${flowType} flow with items:`, currentItems);
				
				// Debug: Log each item's validation status
				currentItems.forEach((item, index) => {
					const isValid = item && 
						item.brandCode && 
						item.sizeLabel && 
						item.typeLabelLong;
					
					console.debug(`🔍 [${flowType}] Item ${index} validation:`, {
						brand: brands[index]?.name || `Index ${index}`,
						isValid,
						item,
						missing: {
							hasItem: !item,
							brandCode: !item?.brandCode,
							sizeLabel: !item?.sizeLabel,
							typeLabelLong: !item?.typeLabelLong,
							firestoreId: !item?.firestoreId // Note: firestoreId is optional
						}
					});
				});
				
				// For Add Item flows, call addItem API for each item
				try {
					// Get all valid items to add (filter out incomplete items)
					// Note: firestoreId is optional - API supports type-only items
					const itemsToAdd = currentItems.filter(item => 
						item && 
						item.brandCode && 
						item.sizeLabel && 
						item.typeLabelLong
					);
					
					console.debug(`📤 [${flowType}] Found ${itemsToAdd.length} items to add:`, itemsToAdd);
					
					if (itemsToAdd.length > 0 && uid && token) {
						const results = [];
						
						// Call addItem API for each item
						for (let i = 0; i < itemsToAdd.length; i++) {
							const itemToAdd = itemsToAdd[i];
							console.debug(`📤 [${flowType}] Adding item ${i + 1}/${itemsToAdd.length}:`, itemToAdd);
							
							const result = await addItem(uid, itemToAdd, token);
							results.push(result);
							
							console.debug(`✅ [${flowType}] Item ${i + 1} successfully added:`, result);
							console.debug(`📝 [${flowType}] Item ${i + 1} ID:`, result.itemId);
						}
						
						console.debug(`🎉 [${flowType}] All ${results.length} items successfully added!`);
						
						// After successful item addition, show analysis view to calculate new bestSize
						console.debug(`🔄 [${flowType}] Moving to AnalysisView for bestSize calculation`);
						props.setStep(7);
					} else {
						console.error(`❌ [${flowType}] Missing required data:`, { 
							itemCount: itemsToAdd.length, 
							uid, 
							hasToken: !!token 
						});
					}
				} catch (error) {
					console.error(`❌ [${flowType}] Failed to add item(s):`, error);
					// Handle error (maybe show error message)
				}
			} else {
				console.debug('✅ [CREATE_ACCOUNT] Completing Create Account flow');
				// Original flow logic
				if (showCredentials) {
					props.setStep(6); // Go to CredentialsView
				} else {
					// BrandProduct flow (no credentials) - proceed to analysis with empty email
					console.debug('📊 [CREATE_ACCOUNT] No credentials flow - proceeding to analysis with empty email');
					setEmail(''); // reset email in account creation context
					props.setStep(7); // go to AnalysisView which will call createNewAccount with empty email
				}
			}
		}
	};

	const handleModalClose = () => {
		trackEvent('size_modal_close');
		(document.getElementById('widget-size-modal') as HTMLDialogElement).close();
		setIsSizeModalOpen(false);
	};

	const resetState = () => {
		console.debug("🧹 resetState called");
		
		// Reset loading state and flow determination ref
		setIsFlowLoading(true);
		setIsFlowStable(false);
		setHasInitializedStep(false);
		flowDeterminationRef.current = false; // Reset ref to allow new flow determination
		
		// Reset view duration tracking states
		setCurrentViewStartTime(null);
		setPreviousViewName(null);
		setSizeViewStartTime(null);
		
		// Log gender-related values during reset
		console.debug('🔍 [GENDER_DEBUG] Reset - Gender values:', {
			gender_specific: props.genderSpecific,
			gender_label: props.genderLabel,
			user_gender_label: isAuthenticated && isOnboarded ? props.genderLabel : undefined,
			isAuthenticated,
			isOnboarded
		});
		
		// Use new comprehensive gender logic to determine initial step
		const userGender = (isAuthenticated && isOnboarded) ? props.genderLabel : undefined;
		const productGender = props.genderLabel;
		
		const decision = determineShoppingPreferenceLogic(
			props.genderSpecific,
			isAuthenticated,
			isOnboarded,
			userGender,
			productGender
		);

		// Set initial step based on decision
		props.setStep(decision.showShoppingPreferenceView ? 2 : 4);
		
		// Handle gender warning
		setGenderWarning(decision.setGenderWarning);
		
		setBrandIndex(0);
		resetAccountCreationState();
		setSelectedBrands([]);
		setSelectedProducts([]);
		setItems([]);
		
		// Handle user gender setting
		if (decision.shouldSetUserGender && decision.userGenderToSet) {
			const preference = getShoppingPreferenceFromGender(decision.userGenderToSet);
			setShoppingPreference(preference);
		}
	};

	const resetAddItemState = () => {
		console.debug("🧹 [ADD_ITEM] resetAddItemState called - resetting all form values");
		
		// Reset loading state and flow determination ref
		setIsFlowLoading(true);
		setIsFlowStable(false);
		setHasInitializedStep(false);
		flowDeterminationRef.current = false; // Reset ref to allow new flow determination
		
		// Reset view duration tracking states
		setCurrentViewStartTime(null);
		setPreviousViewName(null);
		setSizeViewStartTime(null);
		
		// Reset local RegisterView state
		setBrandIndex(0);
		setSelectedBrands([]);
		setSelectedProducts([]);
		setProductModels([]);
		setBrandPhase('product');
		setIsSizeModalOpen(false);
		setGenderWarning(false); // Reset gender warning
		
		// Reset AccountCreationContext state
		resetAccountCreationState();
		
		// For ADD_ITEM flow, preserve shopping preference based on gender
		if (props.genderLabel === 'male') {
			setShoppingPreference(MENSWEAR_SHOPPING_PREFERENCE);
		} else if (props.genderLabel === 'female') {
			setShoppingPreference(WOMENSWEAR_SHOPPING_PREFERENCE);
		}
		
		console.debug("🧹 [ADD_ITEM] All form state reset completed");
	};

	/*useEffect(() => {
		if (!props.modalIsOpen) {
			resetState();
		}

		return () => {
			resetState();
		};
	}, [props.modalIsOpen]);*/

	useEffect(() => {
		if (!props.modalIsOpen) {
			resetState();
		}

	}, [props.modalIsOpen]);

	useEffect(() => {
	if (props.currentView === View.Login) {
		resetState();
	}
	}, [props.currentView]);

	useEffect(() => {
	if (props.currentView === View.SizeRecommendation) {
		resetState();
	}
	}, [props.currentView]);

	// View duration tracking effects
	useEffect(() => {
		if (isFlowLoading || !isFlowStable) return;

		const viewName = getViewName(props.step);
		if (!viewName) return;

		// Track end of previous view if exists
		if (currentViewStartTime !== null && previousViewName) {
			const duration = Date.now() - currentViewStartTime;
			trackEvent(`${previousViewName}_end`, { duration });
		}

		// Track start of new view
		setCurrentViewStartTime(Date.now());
		trackEvent(`${viewName}_start`);
		setPreviousViewName(viewName);
	}, [props.step, isFlowStable, isFlowLoading]);

	// Track size selection modal duration
	useEffect(() => {
		if (isSizeModalOpen) {
			setSizeViewStartTime(Date.now());
			trackEvent('size_selection_view_start');
		} else if (sizeViewStartTime !== null) {
			const duration = Date.now() - sizeViewStartTime;
			trackEvent('size_selection_view_end', { duration });
			setSizeViewStartTime(null);
		}
	}, [isSizeModalOpen]);

	// Track end event when modal closes
	useEffect(() => {
		if (!props.modalIsOpen && currentViewStartTime !== null && previousViewName) {
			const duration = Date.now() - currentViewStartTime;
			trackEvent(`${previousViewName}_end`, { duration });
			setCurrentViewStartTime(null);
			setPreviousViewName(null);
		}
	}, [props.modalIsOpen]);

	const handlePreviousProductBrand = () => {
		if (brandIndex > 0) {
			setBrandIndex(brandIndex - 1);
		}
	};

	const handleBackStep = () => {
		switch (props.step) {
			case 2:
			setShoppingPreference('');
			props.setStep(0);
			break;
			case 4:
			setSelectedBrands([]);
			setBrands([]);
			setSelectedProducts([]);
			setItems([]);
			setBrandIndex(0);
			// For NEW_USER_ADD_ITEM flow, go back to NewUserFlow (step 0)
			// For ADD_ITEM flow, go back to AddItemView (step 0)
			// For CREATE_ACCOUNT flow, go back to ShoppingPreferenceView (step 2)
			if (isNewUserAddItemFlow) {
				props.setStep(0);
			} else if (isAddItemFlow) {
				props.setStep(0);
			} else {
				props.setStep(2);
			}
			break;
			case 5:
			setSelectedProducts([]);
			setItems([]);
			setBrandIndex(0);
			props.setStep(4);
			break;		case 6:
		const updatedItems = [...items];
		updatedItems[brandIndex] = {
			...updatedItems[brandIndex],
			sizeLabel: '',
		};
		setItems(updatedItems);

		const updatedProducts = [...selectedProducts];
		updatedProducts[brandIndex] = null;
		setSelectedProducts(updatedProducts);
		
		if (showTypeSelection) {
			props.setStep(5);
		} else {
			props.setStep(5); // go back to product view
		}
		break;
		case 7:
		props.setStep(6);
		setBrandIndex(brands.length - 1);
		break;
			case 8:
			props.setStep(7);
			break;
			default:
			props.setStep(props.step - 1);
		}
	};

	return (
		<div className="pop-relative pop-flex pop-flex-col pop-w-full pop-h-full">
			<div
				ref={modalContentRef}
				className="pop-flex-1 pop-overflow-y-auto pop-w-full pop-hide-scrollbar"
				//style={{ maxWidth: '100%', overflowY: 'hidden'}}
			>
			{/* Flow determination loading */}
			{isFlowLoading && (
				<div className="pop-h-full pop-w-full pop-flex pop-items-center pop-justify-center">
					<PopsizeSpinningLoaderLargeStandard username="" />
				</div>
			)}
			
			{/* Gender Warning Display */}
			{!isFlowLoading && isFlowStable && genderWarning && (
				<div className="pop-w-full pop-bg-yellow-100 pop-border-l-4 pop-border-yellow-500 pop-p-4 pop-mb-4">
					<div className="pop-flex">
						<div className="pop-ml-3">
							<p className="pop-text-sm pop-text-yellow-700">
								{t('gender_mismatch_warning')}
							</p>
						</div>
					</div>
				</div>
			)}
			{/* End Gender Warning */}
			{!isFlowLoading && isFlowStable && hasInitializedStep && (
			<AnimatePresence mode="wait">
				<motion.div
					key={props.step}
					initial={{ opacity: 0, x: 10 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: -10 }}
					transition={{ duration: 0.3 }}
					className="pop-h-full pop-w-full"
					ref={modalContentRef}
					//style={{ maxWidth: '100%', overflowY: 'auto' }}
				>
				{/*
			{props.step === 0 && (
				<div className={'pop-flex pop-flex-col pop-h-full pop-justify-between pop-min-h-full'}>
					<IntroView handleNextStep={
						() => props.setStep(props.genderSpecific === 'false' ? 2 : 4)
						} modalIsOpen={props.modalIsOpen} toggleView={props.toggleView} />
				</div>
			)}
				*/}
			{props.step === 0 && isNewUserAddItemFlow && (
				<div className={'pop-flex pop-flex-col pop-h-full pop-justify-between pop-min-h-full'}>
					<NewUserFlow 
						handleNextStep={() => {
							console.debug('🚀 [NEW_USER_ADD_ITEM] Moving from NewUserFlow (step 0) to BrandSelectionView (step 4)');
							props.setStep(4);
						}} 
						modalIsOpen={props.modalIsOpen} 
						toggleView={props.toggleView}
					/>
				</div>
			)}
			{props.step === 0 && isAddItemFlow && (
				<div className={'pop-flex pop-flex-col pop-h-full pop-justify-between pop-min-h-full'}>
					<AddItemView 
						handleNextStep={() => {
							console.debug('🚀 [ADD_ITEM] Moving from AddItemView (step 0) to BrandSelectionView (step 4)');
							props.setStep(4);
						}} 
						modalIsOpen={props.modalIsOpen} 
					/>
				</div>
			)}
			{props.step === 2 && (() => {
				// Determine if we should show shopping preference view using the new logic
				const userGender = (isAuthenticated && isOnboarded) ? props.genderLabel : undefined;
				const productGender = props.genderLabel;
				
				const decision = determineShoppingPreferenceLogic(
					props.genderSpecific,
					isAuthenticated,
					isOnboarded,
					userGender,
					productGender
				);
				return decision.showShoppingPreferenceView;
			})() && (
				<div className={'pop-flex pop-flex-col pop-h-full pop-justify-between pop-min-h-full'}>
					<ShoppingPreferenceView
						handleNextStep={() => props.setStep(4)}
						modalIsOpen={props.modalIsOpen}
					/>
				</div>
			)}
			{props.step === 4 && (
				<div className="pop-h-full pop-w-full pop-overflow-y-auto pop-hide-scrollbar">
					<BrandSelectionView
						handleNextStep={(models) => {
							setProductModels(models ?? []);
							props.setStep(5);
						}}
						modalIsOpen={props.modalIsOpen}
						typeLabelLong={props.typeLabelLong}
						typeLabelDisplay={props.typeLabelDisplay}
						selectedBrands={selectedBrands}
						setSelectedBrands={setSelectedBrands}
						onBrandSelect={toggleBrandSelection}
						isSelected={isBrandSelected}
						partnerId={props.partnerId}
					/>
				</div>
			)}
			{props.step === 5 && showProductSelection && (
				<>
					{brandPhase === 'product' && (
						<div className="pop-flex-1 pop-max-h-[450px] pop-w-full pop-overflow-y-auto pop-hide-scrollbar">
							<ProductSelectionView
								key={`product-${brandIndex}`}
								selectedProduct={selectedProducts[brandIndex]}
								handleProductSelect={handleProductSelectedForBrand}
								modalIsOpen={props.modalIsOpen}
								productModels={productModels}
								brand={brands[brandIndex]}
								handleBackStep={handleBackStep}
								handlePreviousBrand={handlePreviousProductBrand}
								brandIndex={brandIndex}
								typeLabelDisplay={props.typeLabelDisplay}
							/>
						</div>
					)}
				</>
			)}
				{props.step === 5 && showTypeSelection && (
					<div className="pop-flex-1 pop-max-h-[450px] pop-w-full pop-overflow-y-auto">
						<TypeSelectionView
							key={`product-${brandIndex}`}
							selectedProduct={selectedProducts[brandIndex]}
							handleProductSelect={handleTypeSelectedForBrand}
							modalIsOpen={props.modalIsOpen}
							//productModels={productModels}
							brand={brands[brandIndex]}
							handleBackStep={handleBackStep}
							handlePreviousBrand={handlePreviousProductBrand}
							brandIndex={brandIndex}
							typeLabelDisplay={props.typeLabelDisplay}
						/>
					</div>
				)}
			{props.step === 6 && showCredentials && (
				<div className={'pop-h-full pop-w-full pop-overflow-y-auto pop-hide-scrollbar'}>
					<CredentialsView handleNextStep={() => props.setStep(7)} modalIsOpen={props.modalIsOpen} toggleView={props.toggleView} />
				</div>
			)}
			{props.step === 7 && (
				<div className={'pop-flex pop-flex-col'}>
					<AnalysisView 
						handleNextStep={() => {}} 
						modalIsOpen={props.modalIsOpen} 
						toggleView={props.toggleView} 
						centerImageUrl={props.centerImageUrl}
						bestSizeReady={isAddItemFlow || isNewUserAddItemFlow} // For both ADD_ITEM flows, skip account creation
						refreshBestSize={props.refreshBestSize} // Pass refreshBestSize for ADD_ITEM flows
						resetAddItemState={(isAddItemFlow || isNewUserAddItemFlow) ? resetAddItemState : undefined} // Pass reset function for ADD_ITEM flows
					/>
				</div>
			)}
						</motion.div>
			</AnimatePresence>
			)}
		</div>
		<div className="pop-h-[15%] pop-p-4 pop-flex pop-flex-col pop-w-full">
			{!isFlowLoading && isFlowStable && (
				<WidgetNavigationButtons
					step={props.step}
					selectedBrandsLength={selectedBrands.length}
					isAuthenticated={isAuthenticated}
					isOnboarded={isOnboarded}
					handleBackStep={handleBackStep}
					handleNextFromBrandView={clickNextFromBrandView}
					handleSkipProduct={() => handleProductSelectedForBrand(null)}
					toggleView={props.toggleView}
					isTypeSelection={props.step === 5 && showTypeSelection}
					isAddItemFlow={isAddItemFlow || isNewUserAddItemFlow}
				/>
			)}
		</div>
		{isSizeModalOpen && (
			<dialog id="widget-size-modal" className="pop-modal" onClick={handleModalClose}>
				<motion.div
					initial={{ opacity: 0, scale: 0.95 }}
					animate={{ opacity: 1, scale: 1 }}
					exit={{ opacity: 0, scale: 0.95 }}
					transition={{ duration: 0.3, ease: 'easeOut' }}
					className="pop-size-modal-box pop-bg-white pop-rounded-xl pop-p-5 pop-shadow-lg"
					onClick={(e) => e.stopPropagation()}
				>
					<div className={'pop-flex pop-flex-row pop-justify-end pop-items-center pop-w-full'}>
					<button
						onClick={handleModalClose}
						className="pop-absolute pop-top-1 pop-right-1"
					>
						<IoClose size={24} fill="#FFAA00" />
					</button>
					</div>
					<SizeSelectionView
						key={`size-${brandIndex}`}
						//key={`size-${brandIndex}-${selectedProducts[brandIndex]?.firestore_id || 'none'}`}
						handleNextStep={(completedItems) => {
							setIsSizeModalOpen(false);
							// Pass the first completed item to handleSizeSelectedForBrand
							const completedItem = completedItems?.[0];
							handleSizeSelectedForBrand(completedItem);
						}}
						modalIsOpen={props.modalIsOpen}
						brandIndex={brandIndex}
						typeLabelLong={props.typeLabelLong}
						typeLabelDisplay={props.typeLabelDisplay}
						selectedProducts={selectedProducts}
					/>
				</motion.div>
			</dialog>
			)}
		</div>
	);
};

export default RegisterView;
